(function (root, factory) {
  'use strict';

  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) {
    root.TRDFeedingGuidePill = api;
    api.init();
  }
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function safeParse(value) {
    try { return value ? JSON.parse(value) : null; } catch (error) { return null; }
  }

  function normaliseProfiles(value) {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object' && Array.isArray(value.dogs)) return value.dogs;
    if (typeof value === 'string') return normaliseProfiles(safeParse(value));
    return [];
  }

  function isUsableDog(dog) {
    if (!dog || typeof dog !== 'object') return false;
    if (String(dog.profileStatus || '').toLowerCase() === 'memorial') return false;
    if (String(dog.memorializedAt || '').trim()) return false;
    return Boolean(String(dog.name || '').trim()) && Number(dog.dailyRecommendationGrams) > 0;
  }

  function usableDogs(value) {
    return normaliseProfiles(value).filter(isUsableDog);
  }

  function packageGrams(item) {
    var title = String(item && (item.variant_title || item.title) || '');
    var match = title.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(kg|g)\b/i);
    if (match) {
      var count = Number(match[1]);
      var amount = Number(match[2]);
      if (match[3].toLowerCase() === 'kg') amount *= 1000;
      if (count > 0 && amount > 0) return count * amount;
    }
    var configuredGrams = Number(item && item.grams);
    return configuredGrams > 0 ? configuredGrams : 0;
  }

  function cartFoodGrams(cart, eligibleProductIds) {
    var eligible = new Set((eligibleProductIds || []).map(String));
    return (cart && Array.isArray(cart.items) ? cart.items : []).reduce(function (total, item) {
      if (!eligible.has(String(item.product_id))) return total;
      return total + packageGrams(item) * Math.max(0, Number(item.quantity) || 0);
    }, 0);
  }

  function cartGuide(cart, eligibleProductIds) {
    var eligible = new Set((eligibleProductIds || []).map(String));
    var foodSubtotalPence = (cart && Array.isArray(cart.items) ? cart.items : []).reduce(function (total, item) {
      if (!eligible.has(String(item.product_id))) return total;
      var linePrice = Number(item.final_line_price);
      if (!Number.isFinite(linePrice)) linePrice = Number(item.line_price) || 0;
      return total + Math.max(0, linePrice);
    }, 0);
    return {
      foodGrams: cartFoodGrams(cart, eligibleProductIds),
      foodSubtotalPence: foodSubtotalPence,
      totalPence: Math.max(0, Number(cart && cart.total_price) || 0),
      currency: String(cart && cart.currency || 'GBP')
    };
  }

  function household(dogs, planDays, guidePackGrams, minimumPacks, recommendationStep) {
    var current = usableDogs(dogs);
    var dailyGrams = current.reduce(function (total, dog) {
      return total + Number(dog.dailyRecommendationGrams);
    }, 0);
    var rawPacks = dailyGrams > 0 ? (dailyGrams * planDays) / guidePackGrams : 0;
    var step = Math.max(1, Number(recommendationStep) || 1);
    return {
      dogs: current,
      names: current.map(function (dog) { return String(dog.name).trim(); }),
      dailyGrams: dailyGrams,
      recommendedPacks: dailyGrams > 0
        ? Math.max(Number(minimumPacks) || 0, Math.ceil(rawPacks / step) * step)
        : 0
    };
  }

  function formatKg(grams) {
    var kg = Math.max(0, Number(grams) || 0) / 1000;
    return (Number.isInteger(kg) ? kg.toFixed(0) : kg.toFixed(1)) + 'kg';
  }

  function formatDailyWeight(grams) {
    var dailyGrams = Math.max(0, Number(grams) || 0);
    if (dailyGrams < 1000) return Math.round(dailyGrams) + 'g';
    return (dailyGrams / 1000).toFixed(2).replace(/\.?0+$/, '') + 'kg';
  }

  function formatMoney(pence, currency) {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format((Math.max(0, Number(pence) || 0) / 100));
    } catch (error) {
      return '£' + (Math.max(0, Number(pence) || 0) / 100).toFixed(2);
    }
  }

  function interpolate(template, values) {
    return Object.keys(values).reduce(function (output, key) {
      return output.replace(new RegExp('\\{\\{\\s*' + key + '\\s*\\}\\}', 'g'), String(values[key]));
    }, String(template || ''));
  }

  function viewModel(dogs, cartState, config) {
    var strings = config.strings;
    var summary = typeof cartState === 'number'
      ? { foodGrams: cartState, totalPence: 0, currency: 'GBP' }
      : (cartState || { foodGrams: 0, totalPence: 0, currency: 'GBP' });
    var foodGrams = Math.max(0, Number(summary.foodGrams) || 0);
    var householdGuide = household(
      dogs,
      config.planDays,
      config.guidePackGrams,
      config.minimumPacks,
      config.recommendationStep
    );
    if (!householdGuide.dogs.length) {
      return { state: 'no-dog', primary: strings.noDogTitle, secondary: '', action: strings.addDog, actionTarget: 'add-dog' };
    }

    var recommendedGrams = householdGuide.recommendedPacks * config.guidePackGrams;
    var selectedWeight = formatKg(foodGrams);
    var targetWeight = formatKg(recommendedGrams);
    var days = foodGrams > 0 ? Math.floor(foodGrams / householdGuide.dailyGrams) : config.planDays;
    var coverageDays = foodGrams > 0 ? foodGrams / householdGuide.dailyGrams : 0;
    var costPerDayPence = coverageDays > 0
      ? Math.round((Math.max(0, Number(summary.foodSubtotalPence) || 0)) / coverageDays)
      : 0;
    var primary;
    var state = 'initial';

    if (foodGrams <= 0) {
      primary = interpolate(strings.weightRecommended, { weight: targetWeight });
    } else if (foodGrams < recommendedGrams) {
      state = 'progress';
      primary = interpolate(strings.weightProgress, { selected: selectedWeight, target: targetWeight });
    } else if (foodGrams === recommendedGrams) {
      state = 'complete';
      primary = interpolate(strings.weightComplete, { selected: selectedWeight, target: targetWeight });
    } else {
      state = 'above';
      primary = interpolate(strings.weightAbove, { selected: selectedWeight });
    }

    return {
      state: state,
      primary: primary,
      primaryTrailing: foodGrams > 0 ? formatMoney(summary.totalPence, summary.currency) + ' total' : '',
      secondary: foodGrams > 0
        ? interpolate(strings.daysCompact, { days: days })
        : interpolate(strings.emptySummary, {
            days: days,
            weight: formatDailyWeight(householdGuide.dailyGrams)
          }),
      secondaryTrailing: foodGrams > 0
        ? interpolate(strings.perDay, { cost: formatMoney(costPerDayPence, summary.currency) })
        : '',
      action: foodGrams > 0 ? strings.viewBowl : strings.myDogs,
      actionTarget: foodGrams > 0 ? 'cart' : 'dogs'
    };
  }

  function liveBowlViewModel(dogs, cartState, config) {
    var summary = cartState || { foodGrams: 0, totalPence: 0, currency: 'GBP' };
    var currentDogs = usableDogs(dogs);
    if (currentDogs.length) return viewModel(currentDogs, summary, config);

    return {
      state: 'bowl',
      primary: formatKg(summary.foodGrams),
      primaryTrailing: formatMoney(summary.totalPence, summary.currency) + ' total',
      secondary: config.customerId ? config.strings.noDogTitle : config.strings.guestTitle,
      secondaryTrailing: '',
      action: config.strings.viewBowl,
      actionTarget: 'cart'
    };
  }

  function shouldShow(isMincePage, foodGrams) {
    return Boolean(isMincePage) || Number(foodGrams) > 0;
  }

  function readConfig() {
    var node = document.getElementById('trdFeedingGuidePillConfig');
    return node ? safeParse(node.textContent) : null;
  }

  function localProfiles(customerId) {
    if (!customerId) return [];
    try {
      return normaliseProfiles(localStorage.getItem('trd_saved_dog_profiles_v1_' + String(customerId)));
    } catch (error) {
      return [];
    }
  }

  function saveLocalProfiles(customerId, profiles) {
    if (!customerId || !Array.isArray(profiles)) return;
    try {
      localStorage.setItem('trd_saved_dog_profiles_v1_' + String(customerId), JSON.stringify({
        version: 1,
        dogs: profiles,
        updatedAt: new Date().toISOString()
      }));
    } catch (error) {}
  }

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed');
      return response.json();
    });
  }

  function fetchProfiles() {
    return fetch('/apps/my-dogs-profiles', {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (response.status === 401) return null;
      if (!response.ok) throw new Error('Profile request failed');
      return response.json();
    });
  }

  function render(element, model, config) {
    var primary = element.querySelector('[data-feeding-pill-primary]');
    var primaryTrailing = element.querySelector('[data-feeding-pill-primary-trailing]');
    var secondary = element.querySelector('[data-feeding-pill-secondary]');
    var secondaryTrailing = element.querySelector('[data-feeding-pill-secondary-trailing]');
    var secondaryRow = secondary.closest('.trd-feeding-pill__row');
    var action = element.querySelector('[data-feeding-pill-action]');
    primary.textContent = model.primary;
    primaryTrailing.textContent = model.primaryTrailing || '';
    primaryTrailing.hidden = !model.primaryTrailing;
    secondary.textContent = model.secondary;
    secondaryTrailing.textContent = model.secondaryTrailing || '';
    secondaryTrailing.hidden = !model.secondaryTrailing;
    secondaryRow.hidden = !model.secondary && !model.secondaryTrailing;
    action.hidden = !model.action;
    action.textContent = model.action;
    action.href = model.actionTarget === 'cart'
      ? config.cartUrl
      : model.actionTarget === 'add-dog' ? config.addDogUrl : config.myDogsUrl;
    element.classList.toggle('is-complete', model.state === 'complete' || model.state === 'above');
    element.dataset.state = model.state;
    element.setAttribute('aria-busy', 'false');
  }

  function setVisible(element, visible) {
    element.hidden = !visible;
  }

  function init() {
    if (typeof document === 'undefined') return;
    var element = document.getElementById('trdFeedingGuidePill');
    var config = readConfig();
    if (!element || !config || element.dataset.ready === 'true') return;
    element.dataset.ready = 'true';

    if (!config.customerId && config.isMincePage) {
      setVisible(element, true);
      render(element, {
        state: 'guest',
        primary: config.strings.guestTitle,
        secondary: '',
        action: config.strings.logIn
      }, config);
      return;
    }

    var profiles = config.customerId ? usableDogs(config.serverProfiles) : [];
    if (config.customerId && !profiles.length) profiles = usableDogs(localProfiles(config.customerId));
    var refreshTimer = 0;

    function refresh() {
      window.clearTimeout(refreshTimer);
      return fetchCart().then(function (cart) {
        var summary = cartGuide(cart, config.eligibleProductIds);
        var visible = shouldShow(config.isMincePage, summary.foodGrams);
        setVisible(element, visible);
        if (!visible) return;
        render(
          element,
          config.isMincePage
            ? viewModel(profiles, summary, config)
            : liveBowlViewModel(profiles, summary, config),
          config
        );
      }).catch(function () {
        if (!config.isMincePage) {
          setVisible(element, false);
          return;
        }
        render(element, viewModel(profiles, 0, config), config);
      });
    }

    function scheduleRefresh(delay) {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refresh, delay || 120);
    }

    refresh();
    if (config.customerId) {
      fetchProfiles().then(function (payload) {
        if (!payload || payload.ok !== true || !Array.isArray(payload.dogs)) return;
        profiles = usableDogs(payload.dogs);
        saveLocalProfiles(config.customerId, payload.dogs);
        refresh();
      }).catch(function () {});
    }

    document.addEventListener('cart:refresh', function () { scheduleRefresh(0); });
    document.addEventListener('cart:updated', function () { scheduleRefresh(0); });
    document.addEventListener('cart:change', function () { scheduleRefresh(0); });
    document.addEventListener('change', function (event) {
      if (event.target && event.target.matches('input[name="updates[]"], .quantity__input')) scheduleRefresh(350);
    });
    document.addEventListener('click', function (event) {
      if (!event.target || !event.target.closest('button[name="add"], .product-form__submit, .quantity__button, cart-remove-button')) return;
      scheduleRefresh(700);
    });
    window.addEventListener('pageshow', function () { scheduleRefresh(0); });

    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () { scheduleRefresh(0); });
      }
    } catch (error) {}

    api.refresh = refresh;
  }

  var api = {
    init: init,
    normaliseProfiles: normaliseProfiles,
    usableDogs: usableDogs,
    packageGrams: packageGrams,
    cartFoodGrams: cartFoodGrams,
    cartGuide: cartGuide,
    household: household,
    formatKg: formatKg,
    formatDailyWeight: formatDailyWeight,
    formatMoney: formatMoney,
    viewModel: viewModel,
    liveBowlViewModel: liveBowlViewModel,
    shouldShow: shouldShow
  };

  return api;
});
